import { useEffect, useRef, useState, useCallback } from 'react'
import { deckLoaders } from '@/decks/registry'
import type { DeckDoc } from '@/decks/registry'
import { initEngine } from '@/decks/shared/engine.js'
import { initMobile } from '@/decks/shared/mobile.js'
import { isMobileViewport } from '@/decks/shared/viewport.js'
import '@/decks/shared/deck.css'
import './DeckViewer.css'

interface EngineApi {
  goTo: (n: number) => void
  scrollToSlide?: (n: number) => void
}

export interface SlideInfo {
  index: number
  label: string
  title: string
  isDark: boolean
  notes: string
  langs: string[]
}

interface DeckViewerProps {
  slug: string
  initialSlide?: number
  onSlideChange?: (slideIndex: number) => void
  onDeckLoaded?: (slideCount: number, slides: SlideInfo[]) => void
  onEngineReady?: (engine: EngineApi) => void
  onDocsLoaded?: (docs: DeckDoc[]) => void
}

export function DeckViewer({ slug, initialSlide = 0, onSlideChange, onDeckLoaded, onEngineReady, onDocsLoaded }: DeckViewerProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  // Capture the mount-time slide so slide navigation (route changes) doesn't
  // re-run loadDeck / reload the whole deck.
  const initialSlideRef = useRef(initialSlide)
  // Holds the teardown for the live engine + mobile module so the effect
  // cleanup can dispose them without stashing state on the DOM node.
  const cleanupRef = useRef<(() => void) | null>(null)
  const [error, setError] = useState<string | null>(null)

  // isCancelled lets the effect invalidate an in-flight load (StrictMode/dev
  // double-mount race) so two async loads can't leave two live engines fighting
  // over the DOM.
  const loadDeck = useCallback(async (isCancelled: () => boolean) => {
    const loader = deckLoaders[slug]
    if (!loader) {
      setError(`Unknown deck: ${slug}`)
      return
    }

    try {
      const mod = await loader()
      // A newer load (or unmount) superseded this one — bail before touching DOM.
      if (isCancelled()) return

      const root = containerRef.current
      if (!root) return

      // Inject HTML
      root.innerHTML = mod.html

      // Init engine (initial slide comes from the route, not the URL hash)
      const engine = initEngine(root, { initialSlide: initialSlideRef.current })
      const isMobile = isMobileViewport()

      // Extract slide metadata from DOM
      const slideEls = root.querySelectorAll('.slide')
      const slideInfos: SlideInfo[] = Array.from(slideEls).map((el, i) => {
        const label = el.querySelector('.section-label')?.textContent?.trim() || ''
        const h = el.querySelector('h1, h2')
        const title = h?.textContent?.trim() || `Slide ${i + 1}`
        const isDark = el.classList.contains('dark')
        const notes = el.getAttribute('data-notes') || ''
        // 'en' is implicit; extra languages come from .lang-xx blocks.
        const langs = ['en', ...new Set(
          Array.from(el.querySelectorAll('[class*="lang-"]'))
            .flatMap(n => Array.from(n.classList))
            .filter(c => c.startsWith('lang-') && c !== 'lang-en')
            .map(c => c.slice(5)),
        )]
        return { index: i, label, title, isDark, notes, langs }
      })

      // Report slide count + metadata
      if (onDeckLoaded) onDeckLoaded(engine.total, slideInfos)
      if (onDocsLoaded) onDocsLoaded(mod.docs || [])

      // Init mobile scroll mode (with slide change callback)
      const mobile = initMobile(root, {
        onSlideChange: isMobile ? onSlideChange : undefined,
      })

      // Bridge slide changes to React (desktop only — mobile uses its own observer)
      if (!isMobile && onSlideChange) {
        engine.onSlideChange(onSlideChange)
        onSlideChange(engine.getCurrentSlide())
      }

      // Expose navigation to parent — use scrollToSlide on mobile, goTo on desktop
      if (onEngineReady) {
        onEngineReady({
          goTo: isMobile
            ? (n: number) => mobile.scrollToSlide(n)
            : engine.goTo,
        })
      }

      // Init canvas modules
      mod.initDeck(root, engine)

      // Store cleanup so the effect teardown can dispose the engine + mobile.
      cleanupRef.current = () => {
        engine.destroy()
        mobile.destroy()
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load deck')
    }
  }, [slug, onSlideChange, onDeckLoaded, onEngineReady, onDocsLoaded])

  useEffect(() => {
    const root = containerRef.current
    let cancelled = false
    loadDeck(() => cancelled)

    return () => {
      // Invalidate any in-flight load so it won't apply after teardown.
      cancelled = true
      if (cleanupRef.current) {
        cleanupRef.current()
        cleanupRef.current = null
      }
      if (root) root.innerHTML = ''
    }
  }, [loadDeck])

  // A mounted deck renders fullscreen, so lock page scroll while it's up.
  // Home/doc pages (no DeckViewer) stay scrollable. See deck.css.
  useEffect(() => {
    document.body.setAttribute('data-deck-fullscreen', '')
    return () => { document.body.removeAttribute('data-deck-fullscreen') }
  }, [])

  if (error) {
    return <div className="deck-error">{error}</div>
  }

  return (
    <div className="deck-viewport" ref={containerRef} />
  )
}
